import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { LeftRightListType } from '../../../models/ContentManagementInterface';
import { ContentProps } from '../../../models/TopLevelTemplateComponentProps';
import { contentNotSet, headingNotSet, newTextContent } from '../../../services/TemplateUtils';
import BlockFootnotesEditor from './BlockFootnotesEditor';
import InlineRichText from './InlineRichText';
import InlineSectionHeader from './InlineSectionHeader';
import InlineText from './InlineText';
import {
  getLeftRightListItemText,
  updateLeftRightListItemText,
} from './newContentManagementEditorUtils';
import { webListLabelSx, webListValueSx } from './webTextStyles';

interface LeftRightListBlockEditorProps {
  block: LeftRightListType;
  onChange: (props: LeftRightListType['properties']) => void;
  /** Shows the Footnotes panel — toggled from a button in this block's toolbar strip. */
  footnotesOpen?: boolean;
}

export default function LeftRightListBlockEditor({
  block,
  onChange,
  footnotesOpen = false,
}: LeftRightListBlockEditorProps) {
  const { properties } = block;

  const updateItem = (index: number, field: 'heading' | 'text', value: string) => {
    const items = [...properties.items];
    if (field === 'heading') {
      items[index] = {
        ...items[index],
        heading: { ...items[index].heading, content: value },
      };
    } else {
      items[index] = {
        ...items[index],
        text: updateLeftRightListItemText(items[index].text, value),
      };
    }
    onChange({ ...properties, items });
  };

  const addItem = () => {
    onChange({
      ...properties,
      items: [
        ...properties.items,
        {
          heading: newTextContent('') as ContentProps & {
            subHeader?: string | null;
            tag?: string;
          },
          text: newTextContent(''),
        },
      ],
    });
  };

  const removeItem = (index: number) => {
    onChange({
      ...properties,
      items: properties.items.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const itemDescriptions = properties.items.map((item) => getLeftRightListItemText(item.text));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineSectionHeader
        heading={properties.heading}
        onChange={(heading) => onChange({ ...properties, heading })}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {properties.items.map((item, index) => (
          <Box
            key={item.heading.id}
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 2,
              alignItems: 'flex-start',
              py: 1.25,
              borderTop: index === 0 ? '1px solid #eee' : 'none',
              borderBottom: '1px solid #eee',
              position: 'relative',
              '&:hover .kd-controls': { opacity: 1 },
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
              <InlineText
                value={item.heading.content ?? ''}
                unset={headingNotSet(item.heading)}
                onChange={(value) => updateItem(index, 'heading', value)}
                placeholder="Add a label"
                ariaLabel={`Item ${index + 1} label`}
                sx={webListLabelSx}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0, sm: 5 } }}>
              <InlineRichText
                html={itemDescriptions[index]}
                unset={contentNotSet({ id: 'x', content: itemDescriptions[index] })}
                onChange={(htmlValue) => updateItem(index, 'text', htmlValue)}
                placeholder="Add a value"
                ariaLabel={`Item ${index + 1} value`}
                sx={webListValueSx}
              />
            </Box>

            <Box
              className="kd-controls"
              sx={{
                position: 'absolute',
                top: 4,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.5,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.92)',
                opacity: 0,
                transition: 'opacity 0.12s ease',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Item {index + 1}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove item ${index + 1}`}
                onClick={() => removeItem(index)}
                sx={{ color: Palette.warning.buttonBg }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addItem}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add item
      </Button>
      {footnotesOpen && (
        <BlockFootnotesEditor
          footnotes={properties.footnotes}
          onChange={(footnotes) => onChange({ ...properties, footnotes })}
        />
      )}
    </Box>
  );
}
